import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from "react";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import type {
	JobShiftHours,
	RecurringCalculationResult,
	SaveRecurringShiftHoursRequest,
	SaveRecurringShiftHoursResult,
	ShiftHoursSlot,
} from "../../../../services/backend.service";
import { useSaveRecurringShiftHours } from "../../hooks";
import type { ShiftHoursSaveToDepartment } from "./RecurringShiftHoursPanel";
import { defaultShiftHoursSaveSelection, newlyEditedSavableJobs, shiftHoursSavePayload, withSavedDepartmentRows } from "./recurringDisplay";

type Args = {
	result: RecurringCalculationResult | null;
	setResult: Dispatch<SetStateAction<RecurringCalculationResult | null>>;
	edits: Record<string, ShiftHoursSlot[]>;
};

/**
 * "Save to department" for the Calculate Recurring shift-hours panel: which jobs are ticked, the
 * confirm dialog, and the outcome of the last save.
 *
 * Owned by the section (it resets with every new proposal and follows the operator's edits), and
 * split out only so the section stays readable. The rules themselves are the pure helpers in
 * `recurringDisplay.ts`, which is where they are tested.
 */
export function useRecurringShiftHoursSave({ result, setResult, edits }: Args) {
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { mutate: save, isPending: saving } = useSaveRecurringShiftHours();
	// The department the proposal on screen was calculated for — captured with it, never read live at
	// save time, so a proposal can only ever be saved into the department it describes.
	const [proposalDepartmentId, setProposalDepartmentId] = useState<string | null>(null);
	const [selected, setSelected] = useState<string[]>([]);
	const [confirmOpen, setConfirmOpen] = useState(false);
	// What the confirm dialog lists and sends, frozen when it opens. A successful save locks the saved
	// rows, which empties the live payload while the dialog is still animating closed — rendering from
	// the live value made it flip to "Save 0 jobs" on its way out. Kept (not cleared) after closing for
	// the same reason; the next open replaces it.
	const [dialogJobs, setDialogJobs] = useState<SaveRecurringShiftHoursRequest["jobs"]>([]);
	const [saveResult, setSaveResult] = useState<SaveRecurringShiftHoursResult | null>(null);

	// Before the first run there is no proposal, and so no jobs that could be saved.
	const derived: JobShiftHours[] = useMemo(() => (result ? result.shiftHours : []), [result]);
	const payload = useMemo(() => shiftHoursSavePayload(derived, edits, selected), [derived, edits, selected]);

	/** A new proposal starts from the hours the operator actually reviewed, and forgets the last save. */
	const resetForProposal = useCallback((proposal: RecurringCalculationResult, currentEdits: Record<string, ShiftHoursSlot[]>, departmentId: string) => {
		setProposalDepartmentId(departmentId);
		setSelected(defaultShiftHoursSaveSelection(proposal.shiftHours, currentEdits));
		setSaveResult(null);
	}, []);

	/** Correcting a job's hours is a strong signal they should be kept, so it ticks the job for them. */
	const followEdits = useCallback(
		(previousEdits: Record<string, ShiftHoursSlot[]>, nextEdits: Record<string, ShiftHoursSlot[]>) => {
			const added = newlyEditedSavableJobs(derived, previousEdits, nextEdits);
			if (added.length > 0) setSelected((prev) => [...new Set([...prev, ...added])]);
		},
		[derived],
	);

	const toggle = useCallback((jobName: string, checked: boolean) => {
		setSelected((prev) => (checked ? [...new Set([...prev, jobName])] : prev.filter((name) => name !== jobName)));
	}, []);

	// `mutate`, not `mutateAsync`: a failure is already reported by the mutation's onError (snackbar +
	// trackError), and awaiting a rejecting promise in a click handler would leak it unhandled.
	const confirmSave = () => {
		if (!proposalDepartmentId || !result || dialogJobs.length === 0) return;
		const defaults = new Set(defaultShiftHoursSaveSelection(derived, edits));
		const dialogNames = new Set(dialogJobs.map((job) => job.jobName));
		save(
			{
				departmentId: proposalDepartmentId,
				jobs: dialogJobs,
				confirmedCount: derived.filter((entry) => entry.source === "confirmed" && dialogNames.has(entry.jobName)).length,
				explicitlySelectedCount: dialogJobs.filter((job) => !defaults.has(job.jobName)).length,
			},
			{
				onSuccess: (saved) => {
					setSaveResult(saved);
					setConfirmOpen(false);
					setResult((prev) => (prev ? { ...prev, shiftHours: withSavedDepartmentRows(prev.shiftHours, saved) } : prev));
				},
			},
		);
	};

	const openConfirm = () => {
		setDialogJobs(payload);
		setConfirmOpen(true);
	};

	const panelControls: ShiftHoursSaveToDepartment = jobManagementEnabled
		? { mode: "perJob", selected, onToggle: toggle, onSave: openConfirm, saving }
		: { mode: "perRole" };

	return { panelControls, dialogJobs, confirmOpen, closeConfirm: () => setConfirmOpen(false), confirmSave, saving, saveResult, resetForProposal, followEdits };
}
